import React, { type FC, useMemo } from 'react'
import { FileCopy, Person, Security } from '@mui/icons-material'

import { isArray } from '../../../../utils'

import { type CybusRole, type CybusUser, type PermissionedForm, Translation } from '../../../../domain'

import { Autocomplete, Button, type WithIcon, type WithMeta } from '../../common'
import { FormattedTranslation } from '../../Internationalization'
import { EntryInputLabel, GridFormEntry, type GridFormEntryProps } from '../Modal'

type RequiredUsecase = Readonly<{
    applyTemplate(userOrRole: CybusUser | CybusRole): void
    searchTemplates(input: string): void
    selectTemplate(userOrRole: CybusUser | CybusRole | null): void
}>

type Props =
    & Readonly<{ disabled: boolean, usecase: RequiredUsecase }>
    & Pick<PermissionedForm, 'templateInput' | 'templates' | 'selectedTemplate'>
    & Pick<GridFormEntryProps, 'endAdornmentSize' | 'expand'>

const withOptionContent = (option: CybusRole | CybusUser): WithMeta<WithIcon<CybusRole | CybusUser>> => {
    const { icon, permissions } = 'username' in option
        ? { icon: <Person />, permissions: option.allPermissions }
        : { icon: <Security />, permissions: option.permissions }

    return { ...option, icon, meta: <FormattedTranslation id={Translation.TOTAL_PERMISSIONS} values={{ count: permissions.length }} /> }
}

export const PermissionsTemplateSection: FC<Props> = ({ expand, endAdornmentSize = 1, disabled, usecase, templateInput, templates, selectedTemplate }) => {
    const options = useMemo(() => (isArray(templates) ? templates.map(withOptionContent) : templates), [templates])

    return (
        <GridFormEntry
            expand={expand}
            labelSize={3}
            endAdornmentSize={endAdornmentSize}
            label={<EntryInputLabel icon={FileCopy} label={Translation.COPY_PERMISSIONS} />}
            endAdornment={
                <Button
                    data-testid="apply-template-button"
                    variant="legacyAmber"
                    fullWidth
                    disabled={disabled ||
                        !selectedTemplate ||
                        !('username' in selectedTemplate ? selectedTemplate.allPermissions.length : selectedTemplate.permissions.length)}
                    onClick={selectedTemplate ? () => usecase.applyTemplate(selectedTemplate) : undefined}
                >
                    <FormattedTranslation id={Translation.ADD} />
                </Button>
            }
        >
            <Autocomplete<CybusRole | CybusUser>
                data-testid="template"
                options={options}
                getOptionLabel={(option) => ('username' in option ? option.username : option.name)}
                isOptionEqualToValue={(a, b) => a === b || a.id === b.id}
                onSearch={(term) => usecase.searchTemplates(term)}
                noOptionsText={Translation.NO_TEMPLATES_AVAILABLE}
                value={selectedTemplate}
                disabled={disabled}
                // strips the render-only icon/meta fields; destructuring (not Omit) since Omit doesn't distribute over this union
                onSelect={(data) => {
                    if (!data) {
                        usecase.selectTemplate(null)
                        return
                    }

                    const { icon, meta, ...userOrRole } = data
                    usecase.selectTemplate(userOrRole)
                }}
                onOpen={() => usecase.searchTemplates(templateInput || '')}
            />
        </GridFormEntry>
    )
}
